import { useQuery } from '@tanstack/react-query'
import { ApiRequestError } from '@/lib/api/client'
import { ErrorState } from '@/components/shared/ErrorState'
import { LevelEditorDialog } from './LevelEditorDialog'
import type { LevelInput } from '@/lib/api/frameworkConfig'
import type { VersionedLevelLabels, VersionFetcher } from './types'
import { Card, CardContent } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'

interface EditDraftLoaderProps {
  versionId: string
  versionNumber: number
  levelCount: number
  isPending: boolean
  labels: VersionedLevelLabels
  saveLabel: string
  cancelLabel: string
  savingLabel: string
  /** Query key for the specific version fetch. */
  queryKey: readonly unknown[]
  /** Function to fetch version data. */
  fetchVersion: VersionFetcher
  onSubmit: (levels: LevelInput[]) => void
  onCancel: () => void
}

export function EditDraftLoader({
  versionId,
  versionNumber,
  levelCount,
  isPending,
  labels,
  saveLabel,
  cancelLabel,
  savingLabel,
  queryKey,
  fetchVersion,
  onSubmit,
  onCancel,
}: EditDraftLoaderProps) {
  const versionQuery = useQuery({
    queryKey,
    queryFn: () => fetchVersion(versionId),
  })

  if (versionQuery.isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
        <Card className="relative z-10 mx-4 w-full max-w-lg">
          <CardContent className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (versionQuery.error || !versionQuery.data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
        <Card className="relative z-10 mx-4 w-full max-w-lg">
          <CardContent className="py-8">
            <ErrorState
              message={`Failed to load ${labels.editDraft.toLowerCase()}`}
              technicalDetail={
                versionQuery.error instanceof ApiRequestError
                  ? versionQuery.error.message
                  : versionQuery.error?.message
              }
              onRetry={() => versionQuery.refetch()}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <LevelEditorDialog
      mode="edit"
      levelCount={levelCount}
      versionNumber={versionNumber}
      initialLevels={versionQuery.data.levels.map((l) => ({
        level_number: l.level_number,
        label: l.label,
        description: l.description ?? '',
      }))}
      isPending={isPending}
      labels={labels}
      saveLabel={saveLabel}
      createLabel=""
      savingLabel={savingLabel}
      cancelLabel={cancelLabel}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}
